import React, { useState, useEffect } from 'react';
import { Check, Plus, Loader2 } from 'lucide-react';
import { llmsService } from '@/api/llmsService';

interface FirstTimeQuerySelectionProps {
  websiteUrl: string;
  suggestedQueries?: string[];
  onContinue: (selectedQueries: string[]) => void;
}

const LOADING_STEPS = [
  'Exploring your website',
  'Understanding your content',
  'Identifying what users might ask about you',
  'Generating relevant queries',
];

const FirstTimeQuerySelection: React.FC<FirstTimeQuerySelectionProps> = ({
  websiteUrl,
  suggestedQueries = [],
  onContinue,
}) => {
  const [queries, setQueries] = useState<string[]>(suggestedQueries);
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const [newQuery, setNewQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingStep, setLoadingStep] = useState(0);

  useEffect(() => {
    if (!isLoading) {
      setLoadingStep(0);
      return;
    }

    const interval = setInterval(() => {
      setLoadingStep((prev) =>
        prev < LOADING_STEPS.length - 1 ? prev + 1 : prev
      );
    }, 1500);

    return () => clearInterval(interval);
  }, [isLoading]);

  useEffect(() => {
    if (!websiteUrl) {
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();

    const fetchQueries = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const result = await llmsService.generateQuerySuggestions(websiteUrl, controller.signal);
        const queryStrings = result.suggestedQueries.map(q => q.query);
        setQueries(queryStrings);
        setSelectedIndices(new Set(queryStrings.map((_, i) => i)));
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'CanceledError') return;
        setError('Failed to generate queries. You can add your own queries below.');
        console.error('Error fetching query suggestions:', err);
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    };

    fetchQueries();

    return () => controller.abort();
  }, [websiteUrl]);

  const toggleQuery = (index: number) => {
    const newSelected = new Set(selectedIndices);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedIndices(newSelected);
  };

  const addQuery = () => {
    if (newQuery.trim()) {
      const newQueries = [newQuery.trim(), ...queries];
      setQueries(newQueries);
      // Shift all existing indices by 1 and add index 0 for the new query
      const shiftedIndices = new Set(Array.from(selectedIndices).map(i => i + 1));
      shiftedIndices.add(0);
      setSelectedIndices(shiftedIndices);
      setNewQuery('');
    }
  };

  const handleContinue = () => {
    const selected = queries.filter((_, i) => selectedIndices.has(i));
    onContinue(selected);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 max-w-[1440px] mx-auto w-full">
        <div className="flex flex-col gap-10 items-center pt-10 pb-10 px-10">
          <div className="flex flex-col gap-6 items-start justify-center w-full max-w-[1080px]">
            <div className="flex flex-col gap-2 items-start text-foreground text-center w-full">
              <h1 className="font-condensed font-bold leading-[1.1] text-2xl uppercase w-full">
                Select queries to see what AI says about your business
              </h1>
              <p className="font-sans font-normal leading-[1.5] opacity-70 text-base w-full">
                We've generated these queries based on the information available on your site.
              </p>
            </div>

            <div className="bg-card border border-border rounded p-6 w-full">
              <div className="flex flex-col w-full">
                <div className="flex flex-col gap-4 w-full">
                  {/* Add query input */}
                  <div className="flex items-center justify-between w-full">
                    <div className="flex-1 flex items-center bg-white border border-border rounded h-12 px-3 gap-3">
                      <input
                        type="text"
                        value={newQuery}
                        onChange={(e) => setNewQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            addQuery();
                          }
                        }}
                        placeholder="Add a query"
                        className="flex-1 font-sans font-medium text-base text-foreground opacity-50 bg-transparent border-none outline-none"
                      />
                      <button
                        onClick={addQuery}
                        className="bg-secondary rounded flex items-center justify-center w-8 h-8 hover:opacity-80 transition-opacity"
                      >
                        <Plus className="w-3 h-3 text-foreground" />
                      </button>
                    </div>
                  </div>

                  <div className="bg-border h-px w-full" />

                  {/* Loading state */}
                  {isLoading && (
                    <div className="flex flex-col items-center justify-center gap-3 py-8">
                      <Loader2 className="w-8 h-8 text-primary animate-spin" />
                      <p
                        key={loadingStep}
                        className="font-sans font-medium text-sm text-foreground opacity-70 animate-fade-in"
                      >
                        {LOADING_STEPS[loadingStep]}...
                      </p>
                    </div>
                  )}

                  {/* Error state */}
                  {error && !isLoading && (
                    <div className="flex flex-col items-center justify-center gap-2 py-6">
                      <p className="font-sans font-medium text-sm text-red-600">
                        {error}
                      </p>
                    </div>
                  )}

                  {/* Query list */}
                  {!isLoading && queries.map((query, index) => (
                    <React.Fragment key={index}>
                      <div className="flex items-center justify-between w-full">
                        <button
                          onClick={() => toggleQuery(index)}
                          className="flex-1 flex gap-3 items-center text-base text-left hover:opacity-80 transition-opacity"
                        >
                          <div className={`w-5 h-5 rounded border-2 shrink-0 flex items-center justify-center transition-colors ${
                            selectedIndices.has(index)
                              ? 'bg-primary border-primary'
                              : 'bg-white border-foreground/30'
                          }`}>
                            {selectedIndices.has(index) && (
                              <Check className="w-3 h-3 text-white" strokeWidth={3} />
                            )}
                          </div>
                          <p className="font-sans font-medium leading-[1.5] text-foreground">
                            {query}
                          </p>
                        </button>
                      </div>
                      {index < queries.length - 1 && (
                        <div className="bg-border h-px w-full" />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky footer */}
      <div className="sticky bottom-0 bg-white border-t border-border mt-auto">
        <div className="max-w-[1440px] mx-auto px-10 py-4 flex items-center justify-between">
          <span className="font-mono font-medium text-sm tracking-[0.7px] uppercase text-foreground opacity-70">
            {selectedIndices.size} {selectedIndices.size === 1 ? 'query' : 'queries'} selected
          </span>
          <button
            onClick={handleContinue}
            disabled={selectedIndices.size === 0}
            className="bg-primary rounded h-12 px-8 flex items-center justify-center hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="font-mono font-medium text-sm tracking-[0.7px] uppercase text-white">
              Generate Report
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default FirstTimeQuerySelection;
